import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button, Card, Col, Image, Row, Space, Typography } from "antd";
import AMEX from "assets/payments/brands/amex.png";
import MASTERCARD from "assets/payments/brands/mastercard.png";
import VISA from "assets/payments/brands/visa.png";
import OPENPAY from "assets/payments/openpay/openpay.png";
import OPENPAY_SECURITY from "assets/payments/openpay/security.png";
import { toast } from "utils/toast";

import usePageLoading from "hooks/usePageLoading";

import {
  CardGridSkeleton,
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonText,
  SlowStateBanner,
} from "js/components/Loading";
import EmptyCard from "js/components/Memberships/EmptyCard";
import { CreditCardItem } from "js/components/Memberships/types";
import { Button as RetryButton } from "js/components/ui/button";

import { Plan } from "js/pages/Memberships/types";

import { getMembershipPlans, updateMembership } from "../../../api/memberships";
import { getCards } from "../../../api/payments";
import { membershipDataUpdated } from "../../../store/features/memberhsip/membership";
import { loadMembershipData } from "../../../store/features/memberhsip/membershipActions";

const { Title, Text } = Typography;

const DEFAULT_PLAN: Plan = {
  id: "",
  name: "",
  price: 0,
  user_members: 0,
};

const MembershipCheckout: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [cards, setCards] = useState<CreditCardItem[]>([]);
  const [planSelected, setPlanSelected] = useState<Plan>(DEFAULT_PLAN);
  const [cardSelected, setCardSelected] = useState<CreditCardItem | null>(null);
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const hirePlan = searchParams.get("plan");

  const { membershipData, loading: membershipLoading } = useSelector(
    (state: any) => state.entities.membership,
  );
  const { organizationData } = useSelector(
    (state: any) => state.entities.organization,
  );
  const dispatch = useDispatch<any>();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const brandImages: Record<string, string> = {
    visa: VISA,
    mastercard: MASTERCARD,
    american_express: AMEX,
  };

  const handleClick = async () => {
    if (!membershipData?.id || !planSelected?.id || !cardSelected?.id) {
      toast.error("Por favor seleccione un plan y/o tarjeta");
      return;
    }

    setLoading(true);
    try {
      const response = await updateMembership(
        membershipData.id,
        planSelected.id,
        cardSelected.id,
      );
      dispatch(membershipDataUpdated({ ...response, plan: planSelected.name }));
      toast.success("¡Transacción completada satisfactoriamente!");
      setTimeout(() => {
        navigate("/memberships/general");
      }, 2000);
    } catch ({ response }) {
      console.error("Error payment:", response);
      toast.error(response?.data?.error ?? "No se pudo procesar el pago.");
    } finally {
      setLoading(false);
    }
  };

  const loadCheckoutData = useCallback(async () => {
    setIsInitialLoading(true);
    try {
      let currentMembership = membershipData;

      if (
        !currentMembership &&
        !membershipLoading &&
        organizationData?.membership_id
      ) {
        try {
          currentMembership = await dispatch(
            loadMembershipData(organizationData.membership_id),
          ).unwrap();
        } catch (membershipError) {
          console.error("Error loading membership data:", membershipError);
        }
      }

      const [plansResponse, cardsResponse] = await Promise.all([
        getMembershipPlans(),
        getCards(),
      ]);

      const sortedPlans = [...plansResponse].sort((a, b) => a.price - b.price);
      setPlans(sortedPlans);

      setPlanSelected((previousPlan) => {
        if (hirePlan) {
          const targetPlan = sortedPlans.find((plan) => plan.id === hirePlan);
          if (targetPlan) {
            return targetPlan;
          }
        }
        if (previousPlan?.id) {
          return previousPlan;
        }
        const currentPlan = sortedPlans.find(
          (plan) => plan.name === currentMembership?.plan,
        );
        return currentPlan ?? sortedPlans[0] ?? DEFAULT_PLAN;
      });

      setCards(cardsResponse);
      setCardSelected((previousCard) => {
        if (
          previousCard &&
          cardsResponse.some((card) => card.id === previousCard.id)
        ) {
          return previousCard;
        }
        return (
          cardsResponse.find((card) => card.is_default) ??
          cardsResponse[0] ??
          null
        );
      });
    } catch (error: any) {
      console.error("Error loading checkout data:", error);
      if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      }
    } finally {
      setIsInitialLoading(false);
    }
  }, [
    dispatch,
    hirePlan,
    membershipData,
    membershipLoading,
    organizationData,
  ]);

  useEffect(() => {
    void loadCheckoutData();
  }, [loadCheckoutData]);

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading: membershipLoading || isInitialLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    const membershipId =
      membershipData?.id ?? organizationData?.membership_id;
    if (membershipId) {
      void dispatch(loadMembershipData(membershipId));
    }
    void loadCheckoutData();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <RetryButton
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </RetryButton>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = null;

  const contentSkeleton = (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
      <div className="space-y-6 rounded-2xl border border-slate-200/70 bg-white/95 p-6">
        <SkeletonHeading className="h-6 w-40" />
        <SkeletonText className="h-5 w-56" />
        <div className="flex gap-4">
          <SkeletonBlock className="h-24 w-40 rounded-2xl" />
          <SkeletonBlock className="h-24 w-40 rounded-2xl" />
        </div>
        <SkeletonText className="h-5 w-52" />
        <div className="flex flex-wrap justify-center gap-6">
          <SkeletonBlock className="h-32 w-36 rounded-lg" />
          <SkeletonBlock className="h-32 w-36 rounded-lg" />
          <SkeletonBlock className="h-32 w-36 rounded-lg" />
        </div>
      </div>
      <div className="space-y-6 rounded-2xl border border-slate-200/70 bg-white/95 p-6">
        <SkeletonHeading className="h-6 w-32" />
        <SkeletonText className="h-4 w-48" />
        <div className="space-y-3">
          <div className="flex justify-between">
            <SkeletonText className="h-4 w-44" />
            <SkeletonText className="h-4 w-32" />
          </div>
          <div className="flex justify-between">
            <SkeletonText className="h-4 w-36" />
            <SkeletonText className="h-4 w-20" />
          </div>
        </div>
        <SkeletonBlock className="h-10 w-full rounded-md" />
        <div className="flex items-center gap-4">
          <SkeletonBlock className="h-12 w-24 rounded" />
          <SkeletonText className="h-3 w-48" />
        </div>
      </div>
    </div>
  );

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      contentSkeleton={contentSkeleton}
      className="space-y-6"
    >
      <>
        <Row gutter={[30, 30]}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
            <Card>
              <Row justify={"center"}>
                <Title level={3}>Checkout</Title>
              </Row>
              <Row style={{ marginTop: 30 }}>
                <Text strong>Seleccione su tarjeta:</Text>
              </Row>
              <Row style={{ marginTop: 30 }} gutter={[15, 15]}>
                <Space direction={"horizontal"}>
                  {cards.length > 0 ? (
                    cards.map((card) => (
                      <Col key={card.id}>
                        <Card
                          size={"small"}
                          style={{ borderRadius: 14 }}
                          onClick={() => setCardSelected(card)}
                          hoverable
                          className={
                            cardSelected && cardSelected.id === card.id
                              ? "selected-card-report-type"
                              : ""
                          }
                        >
                          <div style={{ textAlign: "right" }}>
                            <Image
                              src={brandImages[card.brand] ?? "error"}
                              width={30}
                              preview={false}
                            />
                          </div>
                          <div>**** **** **** {card.last4}</div>
                        </Card>
                      </Col>
                    ))
                  ) : (
                    <EmptyCard cards={cards} setCards={setCards} />
                  )}
                </Space>
              </Row>
              <Row style={{ marginTop: 30 }}>
                <Text strong>Seleccione un plan:</Text>
              </Row>
              <Row
                style={{ marginTop: 30 }}
                gutter={[30, 30]}
                justify={"center"}
              >
                {plans.map((plan) => (
                  <Col key={plan.id}>
                    <Card
                      style={{ textAlign: "center" }}
                      hoverable={
                        !(
                          plan.price === 0 || membershipData?.plan === plan.name
                        )
                      }
                      title={plan.name}
                      onClick={() => {
                        if (membershipData?.plan === plan.name) {
                          toast.info(
                            `Ya te encuentras en el plan ${plan.name}`,
                          );
                          return;
                        }
                        if (plan.price === 0) {
                          toast.info(
                            `No puedes seleccionar el plan ${plan.name}`,
                          );
                          return;
                        }
                        setPlanSelected(plan);
                      }}
                      className={
                        planSelected?.id === plan.id
                          ? "selected-card-report-type"
                          : ""
                      }
                    >
                      <div>
                        <Title level={4}>${plan.price}</Title>
                      </div>
                      <div>
                        <Text type={"secondary"}>
                          {plan.user_members} Usuarios
                        </Text>
                      </div>
                      {membershipData?.plan === plan.name && (
                        <div>
                          <Text type={"success"}>Plan Actual</Text>
                        </div>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
            <Card>
              <Row justify={"center"}>
                <Title level={3}>Detalles</Title>
              </Row>
              <Row>
                <Text>Detalles del Pago:</Text>
              </Row>
              <Row style={{ marginTop: 30 }}>
                <Col span={12} style={{ textAlign: "left" }}>
                  <Text strong>Tarjeta Seleccionada:</Text>
                </Col>
                {cardSelected && (
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Text strong>**** **** **** {cardSelected.last4}</Text>
                  </Col>
                )}
              </Row>
              <Row>
                <Col span={12} style={{ textAlign: "left" }}>
                  <Text strong>Plan Seleccionado: {planSelected.name}</Text>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                  <Text strong>${planSelected.price}</Text>
                </Col>
              </Row>
              <Row justify={"center"} style={{ marginTop: 30 }}>
                <Button
                  style={{ width: "100%" }}
                  type={"primary"}
                  loading={loading}
                  onClick={handleClick}
                >
                  {loading ? "Procesando Pago..." : "Confirmar Compra"}
                </Button>
              </Row>
              <Row style={{ marginTop: 30 }}>
                <Col span={12}>
                  <Space direction={"vertical"}>
                    <Text>Transacciones realizadas vía:</Text>
                    <Image src={OPENPAY} preview={false} />
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction={"horizontal"}>
                    <Image src={OPENPAY_SECURITY} preview={false} />
                    <Text italic>
                      Tus pagos se realizan de forma segura con encriptación de
                      256 bits
                    </Text>
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </>
    </PageLoadingContainer>
  );
};
export default MembershipCheckout;
